import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Info24px } from "@similarweb/icons";
import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";
import dayjs from "dayjs";

import { translate } from "app/filters/i18nFilter";
import { usePeriodChangesData } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/hooks/usePeriodChangesData";
import {
    getBaseWidgetRequestParams,
    getTopicsFilterParam,
    hasPreviousPeriodWindow,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

import { WhatChangedWidget } from "./WhatChangedWidget";

export const WhatChangedSection = (props: ILLMReportWidget) => {
    const {
        params: { topic: queryTopic },
    } = useCurrentStateAndParams();

    const { campaign, campaignConfig, reportStatus, reportDuration } = props;
    const canShowPreviousPeriod = useMemo(
        () => hasPreviousPeriodWindow(reportDuration, campaign),
        [reportDuration, campaign],
    );
    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;

    const { data, isLoading } = usePeriodChangesData(
        {
            ...getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration),
            topics: getTopicsFilterParam(queryTopic),
        },
        { enabled: canLoadData && canShowPreviousPeriod },
    );

    const shouldShowLoading = (isLoading || !campaign) && reportStatus !== ReportStatus.Error;

    if (!canShowPreviousPeriod || (!shouldShowLoading && !data)) {
        return null;
    }

    return (
        <>
            <Flex
                alignItems="center"
                gap="6px"
                margin="8px 0 0 0"
                data-automation="what-changed-title"
            >
                <Text
                    color={colorsPalettes.carbon[500]}
                    fontVariant="h5"
                >
                    {translate("llm.overview_report.what_changed.title")}
                </Text>
                {data?.prevStartDate && data?.prevEndDate && (
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[300]}
                        alignSelf="end"
                    >
                        {translate("llm.overview_report.what_changed.comparison_period", {
                            period:
                                data.prevStartDate === data.prevEndDate
                                    ? dayjs(data.prevEndDate).format(shortMonthDayAndYear)
                                    : `${dayjs(data.prevStartDate).format(shortMonthDayAndYear)} – ${dayjs(data.prevEndDate).format(shortMonthDayAndYear)}`,
                        })}
                    </Text>
                )}
                <Tooltip
                    content={translate("llm.overview_report.what_changed.tooltip")}
                    placement="top"
                >
                    <Info24px
                        size="sm"
                        color={colorsPalettes.carbon[200]}
                        style={{ flexShrink: 0 }}
                    />
                </Tooltip>
            </Flex>
            <WhatChangedWidget
                {...props}
                periodChangesData={data}
                isLoading={shouldShowLoading}
            />
        </>
    );
};
